import { useId } from "react";
import type { PermissionSection } from "@starsach/shared-types";
import { Hint } from "../../components/Hint";
import { SECTIONS, VERB_COLUMNS, label, sectionRows } from "../../lib/permissions";
import type { TooltipId } from "../../lib/tooltips";

const VERB_HINTS: Partial<Record<string, TooltipId>> = { approve: "roles.approve", request: "roles.request", inspect: "roles.inspect", undo: "roles.undo" };

/**
 * The matrix (ADR-0017 section 6): one card per section with its Enabled
 * switch at the head; beneath, the section's things as rows and the verbs
 * as columns, a checkbox per cell that applies, grayed while the section
 * is off - a thing's verb grants nothing without it. Read-only for a
 * built-in, which is copied to be edited.
 */
export function RoleMatrix({ permissions, onChange, readOnly = false }: { permissions: ReadonlySet<string>; onChange?: (next: Set<string>) => void; readOnly?: boolean }) {
  const id = useId();
  const set = (p: string, on: boolean) => {
    if (!onChange) return;
    const next = new Set(permissions);
    if (on) next.add(p);
    else next.delete(p);
    onChange(next);
  };
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {SECTIONS.map((section) => (
        <SectionCard key={section} section={section} id={`${id}-${section}`} permissions={permissions} readOnly={readOnly} onSet={set} />
      ))}
    </div>
  );
}

function SectionCard({ section, id, permissions, readOnly, onSet }: { section: PermissionSection; id: string; permissions: ReadonlySet<string>; readOnly: boolean; onSet: (p: string, on: boolean) => void }) {
  const enabled = permissions.has(`${section}:enabled`);
  const rows = sectionRows(section);
  const columns = VERB_COLUMNS.filter((v) => rows.some((r) => r.verbs.includes(v)));
  return (
    <section aria-labelledby={`${id}-h`} className={`rounded-xl border p-4 ${enabled ? "border-neutral-200 dark:border-neutral-800" : "border-dashed border-neutral-300 dark:border-neutral-700"}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 id={`${id}-h`} className="text-sm font-semibold">{label(section)}</h3>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" role="switch" aria-checked={enabled} aria-label={`${label(section)} enabled`} checked={enabled} disabled={readOnly} onChange={(e) => onSet(`${section}:enabled`, e.target.checked)} />
          Enabled<Hint id="roles.enabled" label="the section switch" />
        </label>
      </div>
      {rows.length > 0 && (
        <table className={`mt-3 w-full text-xs ${enabled ? "" : "opacity-50"}`}>
          <caption className="sr-only">{label(section)}: what the role may do</caption>
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-neutral-500">
              <th scope="col" className="py-1 pr-2 font-medium">Thing</th>
              {columns.map((v) => (
                <th key={v} scope="col" className="py-1 pr-2 text-center font-medium">
                  {label(v)}
                  {VERB_HINTS[v] && <Hint id={VERB_HINTS[v]!} label={label(v)} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-neutral-100 dark:border-neutral-800">
                <th scope="row" className="py-1.5 pr-2 text-left font-normal">{r.label}</th>
                {columns.map((v) => {
                  const p = `${r.key}:${v}`;
                  if (!r.verbs.includes(v)) return <td key={v} className="py-1.5 pr-2 text-center text-neutral-300 dark:text-neutral-700" aria-hidden="true">·</td>;
                  return (
                    <td key={v} className="py-1.5 pr-2 text-center">
                      <input type="checkbox" aria-label={`${label(v)} ${r.label.toLowerCase()}`} checked={permissions.has(p)} disabled={readOnly || !enabled} onChange={(e) => onSet(p, e.target.checked)} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
